import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, Search, ExternalLink } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage, mediaUrl } from "../../api";
import { formatDate } from "../Blog";

const StatusPill = ({ status }) => (
  <span
    className={`chip !text-[11px] uppercase tracking-wide ${
      status === "published" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"
    }`}
    data-testid="post-status"
  >
    {status}
  </span>
);

export default function AdminPosts() {
  const [posts, setPosts] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (posts || []).filter((p) => (filter === "all" || p.status === filter) && (!s || p.title.toLowerCase().includes(s) || p.category.toLowerCase().includes(s)));
  }, [posts, q, filter]);

  const toggle = async (p) => {
    const status = p.status === "published" ? "draft" : "published";
    try {
      const { data } = await api.patch(`/admin/blog/${p.id}/status`, { status });
      setPosts((list) => list.map((x) => (x.id === p.id ? data : x)));
      toast.success(status === "published" ? "Post published" : "Moved to drafts");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      setPosts((list) => list.filter((x) => x.id !== p.id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const counts = { all: posts?.length || 0, published: posts?.filter((p) => p.status === "published").length || 0, draft: posts?.filter((p) => p.status === "draft").length || 0 };

  return (
    <div data-testid="admin-posts-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">Blog posts</h1>
        </div>
        <Link to="/admin/blog/new" className="btn btn-primary" data-testid="admin-new-post">
          <Plus size={16} className="mr-1" /> New post
        </Link>
      </div>

      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex gap-2" data-testid="admin-posts-filters">
          {["all", "published", "draft"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`h-9 px-4 rounded-full text-sm font-medium border capitalize transition-colors ${
                filter === f ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
              }`}
              data-testid={`admin-filter-${f}`}
            >
              {f} <span className="opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>
        <label className="relative md:ml-auto md:w-72">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fr-muted)]" />
          <input className="fr-input !rounded-full !pl-10 !h-10" placeholder="Search posts" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-posts-search" />
        </label>
      </div>

      <div className="card mt-6 overflow-hidden">
        {posts === null ? (
          <div className="p-10 text-center text-sm text-[var(--fr-muted)]">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-posts-empty">
            <h3 className="font-display font-bold text-lg">No posts here yet</h3>
            <p className="text-sm text-[var(--fr-muted)] mt-1">Create your first article or change the filter.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--fr-border)]" data-testid="admin-posts-list">
            {shown.map((p) => (
              <li key={p.id} className="p-4 md:px-6 flex items-center gap-4 hover:bg-[var(--fr-bg)] transition-colors" data-testid={`admin-post-row-${p.slug}`}>
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-[var(--fr-bg-alt)] flex-shrink-0 hidden sm:block">
                  {p.cover && <img src={mediaUrl(p.cover)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill status={p.status} />
                    <span className="text-xs text-[var(--fr-muted)]">{p.category}</span>
                  </div>
                  <Link to={`/admin/blog/${p.id}`} className="block font-display font-bold mt-1 truncate hover:text-[var(--fr-primary-dark)]" data-testid="admin-post-title">
                    {p.title}
                  </Link>
                  <div className="text-xs text-[var(--fr-muted)] mt-0.5">
                    {formatDate(p.date)} · {p.readTime} · {p.author}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {p.status === "published" && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="icon-btn" title="View on site" data-testid="admin-post-view">
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button type="button" onClick={() => toggle(p)} className="icon-btn" title={p.status === "published" ? "Unpublish" : "Publish"} data-testid="admin-post-toggle">
                    {p.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <Link to={`/admin/blog/${p.id}`} className="icon-btn" title="Edit" data-testid="admin-post-edit">
                    <Pencil size={14} />
                  </Link>
                  <button type="button" onClick={() => remove(p)} className="icon-btn danger" title="Delete" data-testid="admin-post-delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
